import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Subscription, forkJoin, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { WorkflowService } from "../../service/workflow.service";
import { S09WorkflowDesignerComponent } from "./s09-workflow-designer.component";
import {
  S09CreateDraft,
  S09DesignDoc,
  WorkflowDefinition,
  apiTriggerType,
  shortName,
} from "./s09-workflow.models";
import {
  designFromCreateDraft,
  cloneDesign,
  emptyDesign,
  fromLoadFull,
  toSaveFullDesign,
  wfDisplayName,
} from "./s09-workflow-mapper";

@Component({
  selector: "app-s09-workflow-shell",
  templateUrl: "./s09-workflow-shell.component.html",
  styleUrls: ["./s09-workflow.scss"],
  standalone: false,
})
export class S09WorkflowShellComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild(S09WorkflowDesignerComponent) wfDesigner?: S09WorkflowDesignerComponent;
  private actionsPainted = false;
  @Input() moduleKey: any;
  @Input() moduleName: any;
  @Input() clientKey: any;

  view: "list" | "canvas" | "run" = "list";
  /** Landing page: Case lifecycles, then Automations (the workflow list). */
  landTab: "caseflow" | "automation" = "caseflow";
  lifecycleCount = 0;
  newLifecycleToken = 0;
  lifecycleEditing = false;
  shellTab: "workflows" | "templates" | "signals" = "workflows";
  brandName = "SCRAII";
  brandKey: number | null = 200104;
  userName = "";

  definitions: WorkflowDefinition[] = [];
  designMetaById: Record<
    number,
    { code?: string; codes?: string[]; group?: string }
  > = {};
  loading = false;
  showCreate = false;
  cloneSourceId: number | null = null;
  saving = false;
  /** Empty until the first save in this session, then "saved" or "error". */
  saveState: "" | "saved" | "error" = "";
  toast = "";

  currentDef: WorkflowDefinition | null = null;
  doc: S09DesignDoc | null = null;
  draftName = "";

  logsOpen = false;
  logsTitle = "";
  logRuns: any[] = [];
  logSteps: any[] = [];

  private subs: Subscription[] = [];
  private toastTimer: any;
  private saveTimer: any;
  private armTimer: any;
  private suppressAutoSave = false;
  private lastSavedJson = "";
  private queuedSave: {
    defId: number;
    fullName: string;
    description: string;
    design: ReturnType<typeof toSaveFullDesign>;
    snap: string;
  } | null = null;

  constructor(
    private workflowService: WorkflowService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewChecked(): void {
    const ready = !!this.wfDesigner;
    if (ready === this.actionsPainted) return;
    this.actionsPainted = ready;
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    this.userName =
      sessionStorage.getItem("username") ||
      sessionStorage.getItem("user_id") ||
      "";
    try {
      const mod = JSON.parse(sessionStorage.getItem("module") || "{}");
      this.brandName = mod?.client_nm || mod?.brand_nm || "SCRAII";
    } catch {
      this.brandName = "SCRAII";
    }
    this.brandKey = this.resolveBrandKey();
    this.reloadList();
  }

  private resolveBrandKey(): number {
    try {
      const raw = sessionStorage.getItem("brandKeyForFilters");
      if (raw) {
        const parsed = JSON.parse(raw);
        const k = Number(parsed?.brand_key ?? parsed);
        if (k) return k;
      }
    } catch {
      /* ignore */
    }
    return 200104;
  }

  ngOnDestroy(): void {
    clearTimeout(this.saveTimer);
    clearTimeout(this.armTimer);
    this.flushAutoSave();
    this.subs.forEach((s) => s.unsubscribe());
    clearTimeout(this.toastTimer);
  }

  reloadList(): void {
    this.loading = true;
    const sub = this.workflowService.getDefinitions({}).subscribe((defs) => {
      this.definitions = defs || [];
      this.loading = false;
      this.hydrateMeta(this.definitions);
    });
    this.subs.push(sub);
  }

  private hydrateMeta(defs: WorkflowDefinition[]): void {
    if (!defs.length) return;
    const sample = defs.slice(0, 40);
    const calls = sample.map((d) =>
      this.workflowService.loadFull(d.wf_def_id).pipe(
        map((res) => {
          const doc = fromLoadFull(res);
          return {
            id: d.wf_def_id,
            meta: doc
              ? {
                  code: doc.meta.code,
                  codes: doc.meta.codes,
                  group: doc.meta.group,
                }
              : {},
          };
        }),
        catchError(() => of({ id: d.wf_def_id, meta: {} as any }))
      )
    );
    const sub = forkJoin(calls).subscribe((rows) => {
      const mapMeta: typeof this.designMetaById = { ...this.designMetaById };
      rows.forEach((r) => (mapMeta[r.id] = r.meta));
      this.designMetaById = mapMeta;
    });
    this.subs.push(sub);
  }

  openCreate(): void {
    this.cloneSourceId = null;
    this.showCreate = true;
  }

  openClone(def: WorkflowDefinition): void {
    this.cloneSourceId = def.wf_def_id;
    this.showCreate = true;
  }

  onCreate(draft: S09CreateDraft): void {
    this.showCreate = false;
    this.cloneSourceId = null;
    const built$ = draft.sourceWfDefId
      ? this.workflowService.loadFull(draft.sourceWfDefId).pipe(
          map((res) => {
            const source = fromLoadFull(res);
            if (!source) throw new Error("That workflow has no design to copy.");
            return cloneDesign(source, draft);
          })
        )
      : of(designFromCreateDraft(draft));
    const sub = built$
      .pipe(
        switchMap((doc) => {
          const name = wfDisplayName(this.brandName, doc.meta, draft.name);
          return this.workflowService
            .createDefinition({
              wf_name: name,
              description: draft.name,
              created_by: this.normalizeUser(this.userName),
              trigger_type: apiTriggerType(doc.trigger),
            })
            .pipe(
              switchMap((def) => {
                if (!def?.wf_def_id) return of(null);
                return this.workflowService
                  .saveFull(def.wf_def_id, {
                    wf_name: name,
                    description: draft.name,
                    updated_by: this.normalizeUser(this.userName),
                    design: toSaveFullDesign(doc),
                  })
                  .pipe(map(() => def));
              })
            );
        })
      )
      .subscribe(
        (def) => {
          if (!def) return;
          this.showToast(
            draft.sourceWfDefId
              ? `${draft.code} cloned · it opens as a draft`
              : `${draft.code} created · check the trigger, then the steps`
          );
          this.openDef(def);
          this.reloadList();
        },
        () => this.showToast("The workflow could not be created.")
      );
    this.subs.push(sub);
  }

  openDef(def: WorkflowDefinition): void {
    const sub = this.workflowService.loadFull(def.wf_def_id).subscribe((res) => {
      let doc = fromLoadFull(res);
      if (!doc) {
        doc = emptyDesign(
          { code: "NEW", caseType: def.wf_name || "" },
          def.trigger_type || "status"
        );
      }
      const merged: WorkflowDefinition = {
        ...def,
        ...(res?.definition || res?.data?.definition || {}),
      };
      this.suppressAutoSave = true;
      this.saveState = "";
      this.currentDef = merged;
      this.doc = doc;
      this.draftName =
        shortName(merged.wf_name || "", doc.meta.code) ||
        merged.wf_name ||
        "";
      this.shellTab = "workflows";
      this.view = "canvas";
      clearTimeout(this.armTimer);
      this.armTimer = setTimeout(() => {
        this.lastSavedJson = this.canvasSnapshot();
        this.suppressAutoSave = false;
      });
    });
    this.subs.push(sub);
  }

  backToList(): void {
    this.flushAutoSave();
    this.view = "list";
    this.shellTab = "workflows";
    this.currentDef = null;
    this.doc = null;
    this.reloadList();
  }

  setLandTab(tab: "caseflow" | "automation"): void {
    this.landTab = tab;
    this.logsOpen = false;
    this.showCreate = false;
  }

  newLifecycle(): void {
    this.landTab = "caseflow";
    this.newLifecycleToken++;
  }

  setShellTab(tab: "workflows" | "templates" | "signals"): void {
    // Tabs only exist in edit mode; keep canvas session while switching panels.
    if (this.view !== "canvas") return;
    this.shellTab = tab;
  }

  onDocChange(doc: S09DesignDoc): void {
    this.doc = doc;
    this.scheduleAutoSave();
  }

  onDraftName(name: string): void {
    this.draftName = name;
    this.scheduleAutoSave();
  }

  private scheduleAutoSave(): void {
    if (this.suppressAutoSave || this.view !== "canvas" || !this.currentDef) return;
    clearTimeout(this.saveTimer);
    this.saveState = "";
    this.saveTimer = setTimeout(() => this.saveCanvas(true), 800);
  }

  private flushAutoSave(): void {
    clearTimeout(this.saveTimer);
    this.saveCanvas(true);
  }

  private canvasSnapshot(): string {
    if (!this.doc) return "";
    return JSON.stringify({
      name: this.draftName,
      design: toSaveFullDesign(this.doc),
    });
  }

  saveCanvas(auto = false): void {
    clearTimeout(this.saveTimer);
    const def = this.currentDef;
    const doc = this.doc;
    if (!def || !doc) return;
    const snap = this.canvasSnapshot();
    const fullName = wfDisplayName(this.brandName, doc.meta, this.draftName);
    const payload = {
      defId: def.wf_def_id,
      fullName,
      description: this.draftName,
      design: toSaveFullDesign(doc),
      snap,
    };
    if (snap === this.lastSavedJson) {
      if (snap) this.saveState = "saved";
      return;
    }
    if (this.saving) {
      this.queuedSave = payload;
      return;
    }
    this.postCanvas(payload, auto);
  }

  openRun(): void {
    const def = this.currentDef;
    const doc = this.doc;
    if (!def || !doc) return;
    const snap = this.canvasSnapshot();
    if (snap === this.lastSavedJson) {
      this.view = "run";
      return;
    }
    this.saving = true;
    this.workflowService
      .saveFull(def.wf_def_id, {
        wf_name: wfDisplayName(this.brandName, doc.meta, this.draftName),
        description: this.draftName,
        updated_by: this.normalizeUser(this.userName),
        design: toSaveFullDesign(doc),
      })
      .subscribe((res) => {
        this.saving = false;
        if (!res) {
          this.saveState = "error";
          this.showToast("Save the workflow before running it.");
          return;
        }
        this.lastSavedJson = snap;
        this.saveState = "saved";
        this.view = "run";
      });
  }

  private postCanvas(
    payload: {
      defId: number;
      fullName: string;
      description: string;
      design: ReturnType<typeof toSaveFullDesign>;
      snap: string;
    },
    auto: boolean
  ): void {
    this.saving = true;
    this.workflowService
      .saveFull(payload.defId, {
        wf_name: payload.fullName,
        description: payload.description,
        updated_by: this.normalizeUser(this.userName),
        design: payload.design,
      })
      .subscribe((res) => {
        this.saving = false;
        const next = this.queuedSave;
        this.queuedSave = null;
        if (!res) {
          this.saveState = "error";
          this.showToast("Save failed");
          if (next && next.snap !== payload.snap) this.postCanvas(next, true);
          return;
        }
        this.lastSavedJson = payload.snap;
        this.saveState = "saved";
        const serverDef = res?.definition || res?.data?.definition;
        if (this.currentDef?.wf_def_id === payload.defId) {
          if (serverDef?.wf_def_id) {
            this.currentDef = {
              ...this.currentDef,
              ...serverDef,
              wf_name: payload.fullName,
            };
          } else if (!auto) {
            this.currentDef = {
              ...this.currentDef,
              wf_name: payload.fullName,
              version: (this.currentDef.version || 1) + 1,
            };
          } else {
            this.currentDef = { ...this.currentDef, wf_name: payload.fullName };
          }
          if (!auto) {
            const reloaded = fromLoadFull(res);
            if (reloaded) {
              this.suppressAutoSave = true;
              this.doc = reloaded;
              this.suppressAutoSave = false;
            }
            this.showToast("Saved");
          }
        }
        if (next && next.snap !== this.lastSavedJson) this.postCanvas(next, true);
      });
  }

  activate(): void {
    if (!this.currentDef || !this.doc) return;
    this.saving = true;
    const fullName = wfDisplayName(
      this.brandName,
      this.doc.meta,
      this.draftName
    );
    const sub = this.workflowService
      .saveFull(this.currentDef.wf_def_id, {
        wf_name: fullName,
        description: this.draftName,
        updated_by: this.normalizeUser(this.userName),
        design: toSaveFullDesign(this.doc),
      })
      .pipe(
        switchMap((res) => {
          if (!res) return of(null);
          return this.workflowService.activateDefinition(
            this.currentDef!.wf_def_id,
            this.normalizeUser(this.userName)
          );
        })
      )
      .subscribe((res) => {
        this.saving = false;
        if (res) {
          this.currentDef = {
            ...this.currentDef!,
            wf_name: fullName,
            status: "ACTIVE",
          };
          this.showToast("Activated");
        }
      });
    this.subs.push(sub);
  }

  pause(): void {
    if (!this.currentDef) return;
    const sub = this.workflowService
      .pauseDefinition(this.currentDef.wf_def_id)
      .subscribe((res) => {
        if (res) {
          this.currentDef = { ...this.currentDef!, status: "PAUSED" };
          this.showToast("Paused");
        }
      });
    this.subs.push(sub);
  }

  deleteDef(def: WorkflowDefinition): void {
    if (!confirm(`Delete workflow "${def.wf_name}"?`)) return;
    const sub = this.workflowService
      .deleteDefinition(def.wf_def_id)
      .subscribe((res) => {
        if (res !== null) this.reloadList();
      });
    this.subs.push(sub);
  }

  openLogs(def: WorkflowDefinition): void {
    this.logsOpen = true;
    this.logsTitle = def.wf_name;
    this.logRuns = [];
    this.logSteps = [];
    const sub = this.workflowService
      .getRuns({ wf_def_id: def.wf_def_id })
      .subscribe((runs) => {
        this.logRuns = runs || [];
        const first = this.logRuns[0];
        if (first?.wf_run_id) {
          this.workflowService
            .getRunStepLogs(first.wf_run_id)
            .subscribe((steps) => {
              this.logSteps = steps || [];
            });
        }
      });
    this.subs.push(sub);
  }

  private showToast(msg: string): void {
    this.toast = msg;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toast = ""), 2600);
  }

  /** Session sometimes stores Cognito blob JSON — API expects a plain string. */
  private normalizeUser(raw: string): string {
    if (!raw) return "";
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed._value != null) {
        return String(parsed._value);
      }
    } catch {
      /* plain string */
    }
    return String(raw);
  }
}
